import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  FormController,
  Link,
  Radio,
  RadioGroup,
  Typography,
  PaginationCompact,
  PaginationDisplayedItemCount,
} from '@one-thd/sui-atomic-components';
import { useFormContext } from 'react-hook-form';
import { track } from '../../../../../analytics/analytics';
import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_OVERLAY,
} from '../../../../../constants/analytics';
import { TICPaymentFormValues } from '../../../../../models/payments';
import {
  ADDRESSES_PAGE_SIZE,
  PAGINATION_THRESHOLD,
  PAGINATION_JUMP_CONTROLS_THRESHOLD,
} from '../../../../../constants/address-constants';
import { useAddress } from '../../../../../hooks/use-address';
import { useDrawer } from '../../../../../hooks/use-drawer';
import { IAddress } from '../../../../../models/address';
import { InstantCheckoutDrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import {
  ADD_DELIVERY_ADDRESS,
  ADD_PAYMENT,
  CONFIRM_PAYMENT,
  EDIT_PAYMENT,
  SELECT_PAYMENT,
} from '../../../../../models/views';
import { useViews } from '../../../../../hooks/use-views';
import { usePayments } from '../../../../../hooks/use-payments';
import { setPaginationAnalytics } from '../../../../../utils/analytics';

interface SelectAddressViewProps {
  addressType: string;
  customGoBack?: () => void;
  setLoadSelectBilling?: (value: boolean) => void;
  setLoadAddBilling?: (value: boolean) => void;
}

export const SelectAddressView = ({
  addressType,
  setLoadSelectBilling,
  customGoBack,
  setLoadAddBilling,
}: SelectAddressViewProps) => {
  const { getValues, setValue } =
    useFormContext<TICPaymentFormValues>?.() ?? {};
  const { hasPayments, defaultPayment } = usePayments();
  const savedPayment = defaultPayment;
  const {
    addresses,
    defaultAddress,
    setSelectedAddress: setInstantCheckoutSelectedAddress,
  } = useAddress();
  const { goToView, currentView } = useViews();
  const { hideDrawer } = useDrawer();
  const [addressesPerPage, setAddressesPerPage] = useState<IAddress[]>([]);
  const [page, setPage] = useState(1);
  const [isPaginationVisible, setIsPaginationVisible] = useState(true);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const billingFormValues = getValues?.();
  const defaultBillingAddress = addresses?.find(
    (address) => address.id === billingFormValues?.billingAddressID,
  );
  const [selectedAddress, setSelectedAddress] = useState<IAddress | undefined>(
    addressType === 'Delivery' ? defaultAddress : defaultBillingAddress,
  );

  useEffect(() => {
    const getAddressesPerPage = addresses.slice(
      ADDRESSES_PAGE_SIZE * (page - 1),
      ADDRESSES_PAGE_SIZE * page,
    );
    const totalAddressesCount = addresses.length;
    const getTotalPageCount = Math.ceil(
      totalAddressesCount / ADDRESSES_PAGE_SIZE,
    );
    const getIsPaginationVisible = totalAddressesCount > PAGINATION_THRESHOLD;
    setAddressesPerPage(getAddressesPerPage);
    setTotalPageCount(getTotalPageCount);
    setIsPaginationVisible(getIsPaginationVisible);
  }, [addresses, page]);

  const handleChangePage = (event: Event, value: number) => {
    const output = {
      target: event.target as HTMLInputElement,
      value,
      overlayName: `${addressType} address`,
      currentPage: page,
    };
    setPaginationAnalytics(output);
    setPage(value);
  };

  const handleCancel = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: `select ${addressType} address`,
      action: 'cancel',
    });
    if (currentView === ADD_PAYMENT || currentView === EDIT_PAYMENT) {
      setLoadSelectBilling?.(false);
    } else hideDrawer();
  };

  const handleSaveAndContinue = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: `select ${addressType} address`,
      action: 'save and continue',
    });
    if (!selectedAddress) {
      return;
    }
    if (addressType === 'Billing') {
      setValue?.('billingAddressID', selectedAddress.id);
      setLoadSelectBilling?.(false);

      return;
    }
    setInstantCheckoutSelectedAddress(selectedAddress);

    if (savedPayment) {
      goToView(CONFIRM_PAYMENT);
    } else {
      const VIEW = hasPayments() ? SELECT_PAYMENT : ADD_PAYMENT;
      const viewName = hasPayments() ? 'select' : 'add';
      track(INSTANT_CHECKOUT_OVERLAY, {
        value: `instant checkout ${viewName} payment method`,
      });
      goToView(VIEW);
    }
  };

  return (
    <div data-testid="select-address-section">
      <InstantCheckoutDrawerHeader
        title={`Select ${addressType} Address`}
        data-testid="drawer-header"
        onClose={() => {
          hideDrawer();
          track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
            overlayType: `select ${addressType} address`,
            action: 'cancel',
          });
        }}
        customGoBack={customGoBack}
      />
      <DrawerBody data-testid="address-drawer">
        <div className="sui-mb-6">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            component="button"
            data-testid="add-new-address-link"
            onClick={() => {
              track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout add new address',
              });
              if (addressType === 'Delivery') {
                goToView(ADD_DELIVERY_ADDRESS);
              } else {
                setLoadAddBilling?.(true);
              }
            }}
          >
            Add New Address
          </Link>
        </div>

        <FormController>
          <RadioGroup
            aria-labelledby="address-radio-buttons-group-label"
            name="address-select-group"
          >
            {addressesPerPage.map((address, index) => {
              const {
                id,
                name: { firstName, lastName },
                postalAddress: {
                  addressLine1,
                  addressLine2,
                  city,
                  stateProvince,
                  zipCode,
                },
                isInstantCheckoutDefault,
              } = address;

              return (
                <div
                  key={`${id}-${index}`}
                  className="sui-flex sui-items-start sui-mb-6"
                  data-testid={`radio-${index}`}
                >
                  <Radio
                    checked={selectedAddress?.id === id}
                    onChange={() => setSelectedAddress(address)}
                    id={`${id}`}
                  />
                  <div>
                    <Typography data-qm-mask="">
                      {firstName} {lastName}
                    </Typography>
                    <Typography data-qm-mask="">
                      {addressLine1}, {addressLine2 && `${addressLine2},`}{' '}
                      {city}, {stateProvince}, {zipCode}
                    </Typography>
                    {isInstantCheckoutDefault && (
                      <Badge variant="strong" color="medium">
                        Default
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        </FormController>

        {isPaginationVisible && (
          <div
            className="sui-flex sui-flex-col sui-items-center sui-gap-y-2"
            data-testid="PaginationWrap"
          >
            <PaginationCompact
              count={totalPageCount}
              page={page}
              onChange={handleChangePage}
              showFirstButton={
                totalPageCount > PAGINATION_JUMP_CONTROLS_THRESHOLD
              }
              showLastButton={
                totalPageCount > PAGINATION_JUMP_CONTROLS_THRESHOLD
              }
            />
            <PaginationDisplayedItemCount
              page={page}
              itemsPerPage={ADDRESSES_PAGE_SIZE}
              items={addresses.length}
            />
          </div>
        )}
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-gap-2">
          <Button
            data-testid="cancel-button"
            fullWidth
            variant="secondary"
            onClick={() => {
              handleCancel();
            }}
          >
            Cancel
          </Button>

          <Button
            data-testid="save-button"
            fullWidth
            variant="primary"
            onClick={handleSaveAndContinue}
          >
            Save and Continue
          </Button>
        </div>
      </DrawerFooter>
    </div>
  );
};
